import { useEffect, useState } from "react";
import { getOrgLocationToolLinks } from "@/lib/adsAssistantClient";
import type { ToolLink } from "@/config/links";
import {
  buildMergedState,
  toToolLinkArray,
  type OrgToolLinksStored,
} from "@/lib/locationToolLinks";

/**
 * Resolves Leads “Location links” from Supabase (via API) with localStorage fallback, then `TOOL_LINKS` defaults.
 */
export function useMergedOrgToolLinks(orgId: string | null): { toolLinks: ToolLink[]; loading: boolean } {
  const [toolLinks, setToolLinks] = useState<ToolLink[]>(() => toToolLinkArray(buildMergedState(undefined, orgId)));
  const [loading, setLoading] = useState(!!orgId);

  useEffect(() => {
    if (!orgId) {
      setToolLinks(toToolLinkArray(buildMergedState(undefined, null)));
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      const r = await getOrgLocationToolLinks();
      if (cancelled) return;
      if (!r.ok) {
        setToolLinks(toToolLinkArray(buildMergedState(undefined, orgId)));
        setLoading(false);
        return;
      }
      const d = r.data as {
        ok?: boolean;
        payload?: OrgToolLinksStored | null;
      };
      const apiPayload =
        d && typeof d === "object" && "payload" in d ? (d.payload as OrgToolLinksStored | null | undefined) : undefined;
      const merged =
        apiPayload === undefined
          ? buildMergedState(undefined, orgId)
          : buildMergedState(apiPayload ?? null, orgId);
      setToolLinks(toToolLinkArray(merged));
      setLoading(false);
    }

    void load();

    function onSaved() {
      void load();
    }
    window.addEventListener("bb-org-tool-links-saved", onSaved);
    return () => {
      cancelled = true;
      window.removeEventListener("bb-org-tool-links-saved", onSaved);
    };
  }, [orgId]);

  return { toolLinks, loading };
}
